import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { stripePromise } from '../stripe';
import { CREATE_CHECKOUT_SESSION_URL } from '../config';
import { PLAN_CONFIG } from '../constants/plans';
import { getCheckoutV2Locale } from '../constants/checkoutV2Locales';
import { setClarityUserId } from '../utils/clarity';
import { CheckoutGeoipInfo } from '../components/CheckoutGeoipInfo';
import { getHostname } from '../utils/getHostname';
import { CheckoutTrustBadges, CheckoutSecureBanner } from '../components/CheckoutTrustBadges';
import './Checkout.css';

function StripeCheckoutForm({ onSuccess, onError, disabled, formData }) {
  const checkoutResult = useCheckout();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (checkoutResult.type !== 'success' || !checkoutResult.checkout) {
      return;
    }

    if (formData?.email && formData?.password) {
      sessionStorage.setItem(
        'stripe_checkout_pending',
        JSON.stringify(formData)
      );
    }

    const { confirm } = checkoutResult.checkout;
    try {
      const result = await confirm();
      if (result && result.error) {
        sessionStorage.removeItem('stripe_checkout_pending');
        onError?.(result.error.message || 'Payment failed');
      } else {
        sessionStorage.removeItem('stripe_checkout_pending');
        onSuccess?.();
      }
    } catch (err) {
      sessionStorage.removeItem('stripe_checkout_pending');
      onError?.(err.message || 'An error occurred');
    }
  };

  if (checkoutResult.type === 'loading') {
    return (
      <div className="checkout-loading">
        Loading payment form...
      </div>
    );
  }
  if (checkoutResult.type === 'error') {
    return (
      <div className="checkout-form-error">
        {checkoutResult.error?.message || 'Failed to load payment form'}
      </div>
    );
  }

  return (
    <>
      <PaymentElement />
      <button
        type="button"
        className="checkout-btn-primary"
        disabled={disabled}
        onClick={handleSubmit}
        style={{ marginTop: '16px', fontSize: '16px' }}
      >
        Pay Now
      </button>
    </>
  );
}

function Checkout() {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isActiveSubscriptionError, setIsActiveSubscriptionError] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [selectedPlanKey, setSelectedPlanKey] = useState('vpn_30_day');

  useDocumentTitle('Checkout');

  const planConfig = PLAN_CONFIG;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (plan && planConfig[plan]) {
      setSelectedPlanKey(plan);
    }
  }, []);

  // Store affid and clickid from query params in localStorage; fallback to existing localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const affid = params.get('affid');
    const clickid = params.get('clickid');
    if (affid != null && affid !== '') {
      localStorage.setItem('vpnnext_affid', affid);
    }
    if (clickid != null && clickid !== '') {
      localStorage.setItem('vpnnext_clickid', clickid);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const pending = sessionStorage.getItem('stripe_checkout_pending');
    if (!pending) return;

    try {
      const { email, password } = JSON.parse(pending);
      sessionStorage.removeItem('stripe_checkout_pending');
      setFormData({ email, password });
      navigate(`/success?session_id=${sessionId}`, { replace: true });
    } catch {
      sessionStorage.removeItem('stripe_checkout_pending');
    }
  }, [navigate]);

  const generatePassword = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `vpn-${Math.random().toString(36).slice(2, 10)}`;
  };

  const amount = planConfig[selectedPlanKey] || planConfig.vpn_30_day;

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();

    if (!email) {
      setPaymentError('Please enter your email address');
      return;
    }

    setClarityUserId(email);
    setPaymentError(null);
    setIsActiveSubscriptionError(false);

    const password = generatePassword();
    setFormData({ email, password });
    setIsLoadingCheckout(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const affid =
        params.get('affid') ?? localStorage.getItem('vpnnext_affid') ?? '';
      const clickid =
        params.get('clickid') ?? localStorage.getItem('vpnnext_clickid') ?? '';

      const returnUrl = `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`;
      const checkoutLocale = getCheckoutV2Locale(params);
      const checkoutBody = {
        customer_email: email,
        password,
        plan: selectedPlanKey,
        product_name: `VPN Subscription - ${amount.label}`,
        price_lookup_key: selectedPlanKey,
        return_url: returnUrl,
        origin: window.location.origin,
        affid,
        clickid,
      };
      if (checkoutLocale !== 'en') {
        checkoutBody.locale = checkoutLocale;
      }

      const response = await fetch(CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutBody),
      });

      const text = await response.text();
      if (!text) {
        setPaymentError('Empty response from server. Check function logs.');
        setIsLoadingCheckout(false);
        return;
      }

      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        setPaymentError('Invalid response from server');
        setIsLoadingCheckout(false);
        return;
      }

      const body = data.body ?? data;
      const statusCode = data.statusCode ?? response.status;

      if (statusCode === 409) {
        setIsActiveSubscriptionError(true);
        setPaymentError(
          body?.error || 'This email already has an active subscription.'
        );
        setIsLoadingCheckout(false);
        return;
      }

      if (body?.error) {
        setPaymentError(body.error);
        setIsLoadingCheckout(false);
        return;
      }

      const secret = body.clientSecret;
      if (!secret) {
        setPaymentError('Invalid response from server (missing clientSecret)');
        setIsLoadingCheckout(false);
        return;
      }

      setClientSecret(secret);
      if (body.sessionId) setCheckoutSessionId(body.sessionId);
    } catch (err) {
      setPaymentError(
        err.message || 'Failed to initialize checkout. Please try again.'
      );
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentError(null);
    const sessionId = checkoutSessionId;
    setClientSecret(null);
    setCheckoutSessionId(null);
    if (sessionId) {
      navigate(`/success?session_id=${sessionId}`, { replace: true });
    } else {
      setPaymentError(
        'Missing session. Please go to the success page from your payment confirmation email or try again.'
      );
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-card">
        <div className="checkout-geoip-slot">
          <CheckoutGeoipInfo />
        </div>
        <h1 className="checkout-header-title">
          Complete Your Registration
        </h1>
        <h2 className="checkout-header-subtitle">
          Secure payment processed worldwide
        </h2>
        <div className="checkout-columns">
          <div className="checkout-left">
            <div className="checkout-logo-row checkout-logo-row--start">
              <img
                src="/images/logo.svg"
                alt="VPN logo"
                className="checkout-logo"
              />
              <div>
                <div className="checkout-brand-name">
                  {getHostname()}
                </div>
                <div className="checkout-plan-desc">
                  {amount.description}
                </div>
              </div>
            </div>
            <h3 className="checkout-section-title">
              Order summary
            </h3>
            <div className="checkout-order-summary">
              <table className="checkout-order-table">
                <tbody>
                  <tr>
                    <td>Plan</td>
                    <td>{amount.label}</td>
                  </tr>
                  <tr>
                    <td>Billing</td>
                    <td>Once</td>
                  </tr>
                </tbody>
              </table>
              <div className="checkout-order-total">
                <span>Total due today</span>
                <span>${amount.display}</span>
              </div>
              <CheckoutSecureBanner />
            </div>
          </div>
          <div className="checkout-right">
            <form className="checkout-form" onSubmit={handleProceedToPayment}>
              <label className="checkout-label">
                Email address{' '}
                <span className="checkout-required">*</span>
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                required
                className="checkout-input"
              />

              {clientSecret ? (
                <CheckoutProvider
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    elementsOptions: {
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          colorPrimary: '#2563eb',
                          borderRadius: '8px',
                        },
                      },
                    },
                  }}
                >
                  <div className="checkout-account-box">
                    <span className="checkout-account-label">
                      Account:
                    </span>{' '}
                    {formData.email}
                  </div>
                  {paymentError && (
                    <p className="checkout-error">
                      {paymentError}
                    </p>
                  )}
                  <StripeCheckoutForm
                    onSuccess={handlePaymentSuccess}
                    onError={setPaymentError}
                    disabled={false}
                    formData={formData}
                  />
                </CheckoutProvider>
              ) : (
                <>
                  <button
                    type="submit"
                    className="checkout-btn-primary"
                    disabled={isLoadingCheckout}
                  >
                    {isLoadingCheckout
                      ? 'Loading...'
                      : 'Continue to secure payment'}
                  </button>
                  {paymentError && (
                    <div
                      className={`checkout-error${isActiveSubscriptionError ? ' checkout-error--warning' : ''}`}
                      style={{ marginTop: '12px' }}
                    >
                      {paymentError}
                      {isActiveSubscriptionError && (
                        <div style={{ marginTop: '10px' }}>
                          <Link to="/login" className="checkout-link">
                            Log in instead →
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="checkout-legal">
                By continuing you agree to our <a href="/tos" className="checkout-link">Terms of Service</a> &amp; <a href="/privacy-policy" className="checkout-link">Privacy Policy</a>.
              </div>
              <CheckoutTrustBadges
                labels={{
                  secure: 'Secure',
                  encrypted: 'Encrypted',
                  pciDss: 'PCI DSS',
                }}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;

